import { useOutletContext, useNavigate } from "react-router-dom";
import { EnrollmentProgress } from "../backend";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { BookOpen, Trophy, Clock } from "lucide-react";

interface ContextType {
  enrollment: EnrollmentProgress | undefined;
  setEnrollment: (e: EnrollmentProgress) => void;
}

export default function CourseOverview() {
  const { enrollment } = useOutletContext<ContextType>();
  const navigate = useNavigate();

  // Calculate progress (mock logic for now)
  const progressPercent = enrollment?.last_visited_lesson_id ? 35 : 0;

  const handleContinue = () => {
    if (enrollment?.last_visited_lesson_id) {
      navigate(
        `/job-recognition-course/lesson/${enrollment.last_visited_lesson_id}`
      );
    } else {
      // Default to first lesson if no progress
      navigate(`/job-recognition-course/lesson/201`);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-[#471999]">German Nursing B1</h1>
        <p className="text-gray-600 text-lg">
          Master the essential language skills for your nursing career in
          Germany.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Overall Progress
            </CardTitle>
            <Trophy className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{progressPercent}%</div>
            <Progress value={progressPercent} className="mt-2 h-2" />
            <p className="text-xs text-muted-foreground mt-2">
              {progressPercent >= 100 ? "Course Completed!" : "Keep going!"}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Modules Completed
            </CardTitle>
            <BookOpen className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">0/3</div>
            <p className="text-xs text-muted-foreground mt-2">
              Finish all modules to get certified.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Est. Time Left
            </CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">4h 30m</div>
            <p className="text-xs text-muted-foreground mt-2">
              Based on your current pace.
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="bg-[#f5f2ff] border-[#471999]/20">
        <CardContent className="p-6 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-[#471999] mb-1">
              Ready to continue?
            </h3>
            <p className="text-gray-600">Pick up exactly where you left off.</p>
          </div>
          <Button
            size="lg"
            className="bg-[#471999] hover:bg-[#38147a]"
            onClick={handleContinue}
          >
            {enrollment?.last_visited_lesson_id
              ? "Continue Learning"
              : "Start Course"}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
